import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../api/client'
import { canPublishPublicCopy, canRevise, publishTarget, unpublishTarget, type Destination } from '../../lib/brief'
import { describeError } from '../../lib/describeError'
import { ReviseForm } from '../../pages/ReviseForm'
import { approvePiece, errorCode, putBackUp, sendToEditor, unapprovePiece, takeBackFromEditor, type ChecklistItem, type PieceDetail } from '../api'
import { deskHref, startFrom, versionHref } from '../links'
import { useVersionsOn } from '../useVersionsOn'
import { putBackErrorText, putBackIsStale } from '../live'
import { APPROVAL, chipClass, HANDOFF, stageLabel } from '../labels'
import { canPublish as mayPublish, useEditorialMe } from '../me'
import type { SaveState } from './editorTypes'
import { blockingCount, canApprove, canOpenPublish, canPutBackUp, canUnapprove, canSendToEditor, canSetAside, offersAgentActions, REVISE_WAIT, reviseBlockedReason, SAVE_FIRST, saveStateText, showDestination } from './pieceHelpers'
import { PublishDialog } from './PublishDialog'
import { SaveAndPublish } from './SaveAndPublish'
import { btn, btnPrimary, btnSmall, errorText, focus } from './ui'

const SD = '/api/lab/section-drafts'

type Props = {
  detail: PieceDetail
  sectionLabel: string
  /** The title's section.path; links are built from it. */
  path: string
  publicationSlug: string
  saveState: SaveState
  saveReason: string | null
  onReload: () => void
  /** Resend the refused field (Try again). */
  onRetry: () => void
  /** Saves what waits first; false refuses the write (edits would be stranded). */
  beforeWrite: () => Promise<boolean>
  /** A copy of the piece a write here returned, for the page to adopt. */
  onDetail: (d: PieceDetail) => void
  /** Who will read it, as on screen (an unsaved choice included). */
  destination: Destination
  /** Saved at once through the page's autosave. */
  onDestination: (d: Destination) => void
  /** A draft is running for this title (Revise would be refused). */
  running: boolean
  /** A Revise or fact recheck of this piece holds it: every write here waits. */
  locked: boolean
  onChanged: () => void
  onReviseStarted: () => void
  /** Show me / Fix on an open publish item. */
  onShowItem: (item: ChecklistItem) => void
}

export function TopBar({ detail: d, sectionLabel, path, publicationSlug, saveState, saveReason, onReload, onRetry, beforeWrite, onDetail, destination: dest, onDestination, running, locked, onChanged, onReviseStarted, onShowItem }: Props) {
  const [revising, setRevising] = useState(false)
  const [menu, setMenu] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  /** Where the article lives, once Save and publish has put the edits on it. */
  const [savedUrl, setSavedUrl] = useState<string | null>(null)
  const me = useEditorialMe()
  const versionsOn = useVersionsOn(publicationSlug)
  // A writer drafts; an editor publishes. Unknown (loading) shows neither side's buttons.
  const editor = mayPublish(me.data)
  const writer = me.data !== undefined && !editor
  const sent = !!d.sent_to_editor_at
  const sentByMe = sent && me.data !== undefined && d.sent_to_editor_by_id === me.data.user_id
  useEffect(() => {
    if (!notice) return
    const t = setTimeout(() => setNotice(null), 6000)
    return () => clearTimeout(t)
  }, [notice])
  const superseded = d.status === 'superseded'
  // An edit draft saves over its live article (Save and publish); it never publishes as a piece.
  const isEdit = d.edits_article_id != null
  const editOpen = isEdit && d.status === 'approved' && !d.edit_stale
  const unsaved = saveState !== 'saved'
  const reviseBlocked = reviseBlockedReason(d.status, running, unsaved, canRevise)

  /** A write outside the autosave: save the edits first, then adopt what the server returns. */
  const write = (path: string, body?: unknown) => async () => {
    if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
    return api.post<PieceDetail>(`${SD}/${d.id}/${path}`, body)
  }
  const adopted = (r: PieceDetail) => { onDetail(r); onChanged() }
  const setAside = useMutation({ mutationFn: write('reject', { note: null }), onSuccess: adopted })
  const takeDown = useMutation({ mutationFn: write('unpublish', { note: null }), onSuccess: adopted })
  const publishPublic = useMutation({ mutationFn: write('publish-public'), onSuccess: adopted })
  // Publish again: the same WordPress posts go back up (the put-back-up route).
  const putBack = useMutation({ mutationFn: async () => { if (!(await beforeWrite())) throw new Error(SAVE_FIRST); return putBackUp(d.id) },
    onSuccess: (r) => { adopted(r); setNotice('It is back up.') },
    // someone else put it back up: reload, so the page stops offering it
    onError: (e) => { if (putBackIsStale(e)) onChanged() } })
  const send = useMutation({ mutationFn: async () => { if (!(await beforeWrite())) throw new Error(SAVE_FIRST); return sendToEditor(d.id) },
    onSuccess: (r) => { adopted(r); setNotice('Sent. An editor of this title will pick it up.') } })
  const back = useMutation({ mutationFn: (note: string | null) => takeBackFromEditor(d.id, note),
    onSuccess: (r) => { adopted(r); setNotice(editor ? 'Sent back to the writer.' : 'Taken back. It is yours to work on.') } })
  // Approve: the server refuses with blocking_items while checklist items are open; the editor
  // may approve anyway (same rule as the Publish dialog's acknowledgement).
  const approve = useMutation({
    mutationFn: async () => {
      if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
      try {
        return await approvePiece(d.id)
      } catch (e) {
        if (errorCode(e) !== 'blocking_items') throw e
        const open = blockingCount(d.checklist ?? [])
        if (!window.confirm(APPROVAL.confirmOpen(open || 1))) throw new Error('cancelled')
        return approvePiece(d.id, true)
      }
    },
    onSuccess: (r) => { adopted(r); setNotice('Approved. It can now be published or scheduled.') },
  })
  const unapprove = useMutation({ mutationFn: () => unapprovePiece(d.id),
    onSuccess: (r) => { adopted(r); setNotice('Back to review.') } })
  const cancelled = approve.error instanceof Error && approve.error.message === 'cancelled'
  const other = (cancelled ? null : approve.error) ?? unapprove.error ?? setAside.error ?? takeDown.error ?? publishPublic.error ?? send.error ?? back.error
  const error = other ? describeError(other) : putBack.error ? putBackErrorText(putBack.error) : null
  const savedDest: Destination = d.destination ?? d.brief?.destination ?? 'section'

  const options: { key: Destination; label: string }[] = [
    { key: 'section', label: sectionLabel }, { key: 'membership', label: 'Members only' }, { key: 'both', label: 'Both' },
  ]

  return (
    <header className="sticky top-0 z-20 border-b border-ink-500 bg-ink-700 px-4 font-ui md:px-8">
      <div className="mx-auto flex max-w-[1360px] flex-wrap items-center justify-between gap-3 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Link to={deskHref(path)} className={`${btn} min-h-9 px-3`}>← Desk</Link>
          <span className={chipClass(d.status)}>{superseded ? (isEdit ? 'Saved to the live article' : 'Earlier version') : stageLabel(d.status)}</span>
          {sent && canSendToEditor(d.status) && (
            <span className="inline-flex h-6 items-center whitespace-nowrap rounded-full bg-radar-cyan/15 px-2.5 font-ui text-[12px] font-semibold text-radar-cyan">
              {HANDOFF.sentBy(d.sent_to_editor_by_name)}
            </span>
          )}
          {!superseded && (
            <span role="status" className={`inline-flex items-center gap-2 text-[13px] ${saveState === 'error' || saveState === 'stale' ? 'font-semibold text-radar-red' : 'text-paper-dim'}`}>
              {saveStateText(saveState)}
              {saveState === 'error' && <button type="button" className={btnSmall} onClick={onRetry}>Try again</button>}
              {saveState === 'error' && saveReason && <span className="font-normal text-paper-dim">({saveReason})</span>}
              {saveState === 'stale' && (
                <button type="button" className={btnSmall}
                        onClick={() => { if (window.confirm('Reload the newer copy? Your unsaved changes on this page will be lost.')) onReload() }}>
                  Reload it
                </button>
              )}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {showDestination(d.status, publicationSlug) && !isEdit && (
            <div role="radiogroup" aria-label="Who will read it" className="flex gap-0.5 rounded-[9px] bg-ink-600 p-[3px]">
              {options.map((o) => (
                <button key={o.key} type="button" role="radio" aria-checked={dest === o.key}
                        disabled={saveState === 'stale' || locked || !editor}
                        title={writer ? 'An editor chooses who reads it.' : undefined}
                        onClick={() => { if (dest !== o.key) onDestination(o.key) }}
                        className={`min-h-[34px] rounded-[7px] px-3 text-[13px] ${focus} ${
                          dest === o.key ? 'bg-ink-700 font-bold text-paper shadow-sm' : 'font-semibold text-paper-dim hover:text-paper'}`}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
          {!superseded && !isEdit && offersAgentActions(d.origin) && (
            <div className="relative" title={reviseBlocked ?? undefined}>
              <button type="button" className={btn} aria-expanded={revising} aria-describedby={reviseBlocked ? 'revise-blocked' : undefined}
                      disabled={!!reviseBlocked && !revising}
                      onClick={() => { setMenu(false); setRevising((x) => !x) }}>
                Revise…
              </button>
              {reviseBlocked && <span id="revise-blocked" className="sr-only">{reviseBlocked}</span>}
              {revising && canRevise(d.status) && (
                <div className="absolute right-0 top-full z-30 mt-2 w-[min(420px,calc(100vw-32px))] rounded-xl border border-ink-500 bg-ink-700 p-1 shadow-lg">
                  <ReviseForm draftId={d.id} currentFormat={d.brief?.format} bodyHtml={d.body_html} blockedHint={reviseBlocked} onClose={() => setRevising(false)}
                              onStarted={() => { setRevising(false); onReviseStarted() }} />
                </div>
              )}
            </div>
          )}
          {!superseded && (
            <div className="relative">
              <button type="button" className={`${btn} px-3`} aria-label="More actions" aria-haspopup="menu" aria-expanded={menu}
                      onClick={() => { setRevising(false); setMenu((x) => !x) }}>•••</button>
              {menu && (
                <div role="menu" className="absolute right-0 top-full z-30 mt-2 flex w-64 flex-col rounded-xl border border-ink-500 bg-ink-700 p-1.5 shadow-lg">
                  {editor && canSetAside(d.status) && (
                    <button type="button" role="menuitem" disabled={setAside.isPending || locked}
                            className={`rounded-lg px-3 py-2 text-left text-[14px] font-semibold text-paper hover:bg-ink-600 ${focus}`}
                            onClick={() => {
                              setMenu(false)
                              const ask = isEdit ? 'Set this edit aside? The live article stays exactly as it is.' : 'Set this piece aside? It frees the subject for a new piece.'
                              if (window.confirm(ask)) setAside.mutate()
                            }}>
                      {isEdit ? 'Set this edit aside' : 'Set this piece aside'}
                    </button>
                  )}
                  {!isEdit && (
                    <Link role="menuitem" to={startFrom(path, d.id)}
                          className={`rounded-lg px-3 py-2 text-[14px] font-semibold text-paper no-underline hover:bg-ink-600 ${focus}`}>
                      Draft this subject again
                    </Link>
                  )}
                  {versionsOn && !isEdit && (
                    <Link role="menuitem" to={versionHref(path, { draftId: d.id })}
                          className={`rounded-lg px-3 py-2 text-[14px] font-semibold text-paper no-underline hover:bg-ink-600 ${focus}`}>
                      Make a version…
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
          {editor && canPublishPublicCopy({ status: d.status, destination: d.destination ?? d.brief?.destination ?? 'section', error: d.error, wp_post_id: d.wp_post_id, member_wp_post_id: d.member_wp_post_id }) && (
            <button type="button" className={btn} disabled={publishPublic.isPending || locked} onClick={() => publishPublic.mutate()}>
              {publishPublic.isPending ? 'Publishing…' : 'Publish the public copy'}
            </button>
          )}
          {editor && d.status === 'published' && (
            <button type="button" className={btn} disabled={takeDown.isPending || locked}
                    onClick={() => { if (window.confirm(`Take this piece down? It is removed from ${unpublishTarget(d, sectionLabel)} now: each WordPress post becomes a draft.`)) takeDown.mutate() }}>
              {takeDown.isPending ? 'Taking down…' : 'Take down…'}
            </button>
          )}
          {editor && canPutBackUp(d.status) && (
            <button type="button" className={btnPrimary} disabled={putBack.isPending || locked}
                    onClick={() => { if (window.confirm(`Put this piece back up on ${publishTarget(savedDest, sectionLabel)}?`)) putBack.mutate() }}>
              {putBack.isPending ? 'Putting it back…' : 'Put back up'}
            </button>
          )}
          {sent && canSendToEditor(d.status) && editor && (
            <button type="button" className={btn} disabled={back.isPending || locked}
                    onClick={() => {
                      const note = window.prompt('Send this piece back to the writer? Add a note for them if you like.', '')
                      if (note !== null) back.mutate(note.trim() || null)
                    }}>
              {back.isPending ? 'Sending back…' : HANDOFF.sendBack}
            </button>
          )}
          {writer && sentByMe && canSendToEditor(d.status) && (
            <button type="button" className={btn} disabled={back.isPending || locked} onClick={() => back.mutate(null)}>
              {back.isPending ? 'Taking back…' : HANDOFF.takeBack}
            </button>
          )}
          {writer && sent && canSendToEditor(d.status) && (
            <span className="text-[13px] text-paper-dim">{APPROVAL.waiting}</span>
          )}
          {writer && !sent && canSendToEditor(d.status) && (
            <button type="button" className={btnPrimary} disabled={unsaved || locked || send.isPending}
                    title={locked ? REVISE_WAIT : unsaved ? SAVE_FIRST : undefined}
                    onClick={() => { setMenu(false); setRevising(false); send.mutate() }}>
              {send.isPending ? 'Sending…' : HANDOFF.send}
            </button>
          )}
          {editor && canApprove(d, me.data) && (
            <button type="button" className={btnPrimary} disabled={unsaved || locked || approve.isPending}
                    title={locked ? REVISE_WAIT : unsaved ? SAVE_FIRST : undefined}
                    onClick={() => { setMenu(false); setRevising(false); approve.reset(); approve.mutate() }}>
              {approve.isPending ? 'Approving…' : APPROVAL.approve}
            </button>
          )}
          {editor && canUnapprove(d, me.data) && (
            <button type="button" className={btn} disabled={unapprove.isPending || locked} onClick={() => unapprove.mutate()}>
              {unapprove.isPending ? 'Working…' : APPROVAL.undo}
            </button>
          )}
          {editor && editOpen && (
            <SaveAndPublish detail={d} disabled={unsaved || locked} title={locked ? REVISE_WAIT : unsaved ? SAVE_FIRST : undefined}
                            beforeWrite={beforeWrite} onStale={onChanged} onWebUnchecked={onChanged}
                            onSaved={(r, url) => { onDetail(r); setSavedUrl(url); setNotice('Saved. The live article has your edits.'); onChanged() }} />
          )}
          {editor && !isEdit && canOpenPublish(d.status) && (
            <button type="button" className={btnPrimary} disabled={unsaved || locked} title={locked ? REVISE_WAIT : unsaved ? SAVE_FIRST : undefined}
                    aria-describedby={unsaved ? 'publish-save-first' : undefined}
                    onClick={() => { setMenu(false); setRevising(false); setPublishing(true) }}>
              Publish…
            </button>
          )}
          {editor && !isEdit && unsaved && canOpenPublish(d.status) && <span id="publish-save-first" className="sr-only">{SAVE_FIRST}</span>}
        </div>
      </div>
      {error && <p role="alert" className={`${errorText} mx-auto max-w-[1360px] pb-2`}>{error}</p>}
      {writer && !sent && d.sent_back && (
        <p role="status" className="mx-auto max-w-[1360px] pb-2 font-ui text-[13px] text-paper-dim">
          Sent back to you{d.sent_back.by_name ? ` by ${d.sent_back.by_name}` : ''}{d.sent_back.note ? `: “${d.sent_back.note}”` : '.'}
        </p>
      )}
      {notice && <p role="status" className="mx-auto max-w-[1360px] pb-2 font-ui text-[13px] font-semibold text-paper">{notice}</p>}
      {savedUrl && isEdit && (
        <p className="mx-auto max-w-[1360px] pb-2 font-ui text-[13px] text-paper-dim">
          <a href={savedUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-amber-deep underline">View the live article</a>
        </p>
      )}
      {publishing && (
        <PublishDialog detail={d} onClose={() => setPublishing(false)} onBeforeSubmit={beforeWrite} onShowItem={onShowItem}
                       onPublished={(m) => { setPublishing(false); setNotice(m); onChanged() }} />
      )}
    </header>
  )
}
